import React, {ReactNode} from 'react';
import {Button, Space, Typography} from 'antd';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import './PageContainer.less';

const {Paragraph, Title, Text} = Typography;

export interface PageContainerProps {
  title?: ReactNode;
  subTitle?: string;
  tagline?: string;
  extra?: ReactNode;
  children?: ReactNode;
  showBack?: boolean;
  onBack?: () => void;
  footer?: ReactNode;
  tags?: ReactNode | ReactNode[];
  avatar?: ReactNode;
  breadcrumb?: ReactNode;
  tabs?: ReactNode;
  bordered?: boolean;
  ghost?: boolean;
  colorGap?: boolean;
  /**
   * When true, the header gets symmetric vertical padding (16px top/bottom) —
   * meant for list pages. Default `false` keeps the object-page look with no
   * bottom padding so the title row sits flush against the content below.
   */
  headerPaddingBottom?: boolean;
  className?: string;
  titleClassName?: string;
  headerClassName?: string;
}

const PageContainer: React.FC<PageContainerProps> = ({
                                                       title,
                                                       subTitle,
                                                       tagline,
                                                       extra,
                                                       children,
                                                       showBack = false,
                                                       onBack,
                                                       footer,
                                                       tags,
                                                       avatar,
                                                       breadcrumb,
                                                       tabs,
                                                       bordered = true,
                                                       titleClassName = '',
                                                       headerClassName = '',
                                                       ghost = false,
                                                       colorGap = false,
                                                       headerPaddingBottom = false,
                                                       className = '',
                                                     }) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const renderTags = () => {
    if (!tags) return null;

    const tagArray = Array.isArray(tags) ? tags : [tags];
    return (
      <Space size={8} className="page-container-tags">
        {tagArray.map((tag, index) => (
          <React.Fragment key={index}>{tag}</React.Fragment>
        ))}
      </Space>
    );
  };

  const hasHeader = Boolean(
    title || subTitle || tagline || extra || showBack || avatar || tags || tabs,
  );

  return (
    <div className={`page-container ${ghost ? 'page-container-ghost' : ''} ${colorGap ? 'page-container-color-gap' : ''} ${className}`}>
      {breadcrumb && (
        <div className="page-container-breadcrumb">
          {breadcrumb}
        </div>
      )}

      {hasHeader && (
        <div
          className={`page-container-header ${bordered ? 'page-container-header-bordered' : ''} ${headerPaddingBottom ? 'page-container-header-padded-bottom' : ''} ${headerClassName}`}
        >
          {(title || subTitle || tagline || extra || showBack || avatar || tags) && (
            <div className="page-container-header-content">
              <div className="page-container-header-left">
                {showBack && (
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined/>}
                    onClick={handleBack}
                    className="page-container-back-button"
                  />
                )}
                {avatar && (
                  <div className="page-container-avatar">
                    {avatar}
                  </div>
                )}
                <div className="page-container-header-title-content">
                  {tagline && (
                    <Text className="page-container-tagline">{tagline}</Text>
                  )}
                  {(title || tags) && (
                    <div className="page-container-title-row">
                      {title && (
                        <Title
                          level={2}
                          className={`page-container-title ${titleClassName}`}
                        >
                          {title}
                        </Title>
                      )}
                      {renderTags()}
                    </div>
                  )}
                  {subTitle && (
                    <Paragraph className="page-container-subtitle">
                      {subTitle}
                    </Paragraph>
                  )}
                </div>
              </div>
              {extra && <div className="page-container-header-extra">{extra}</div>}
            </div>
          )}

          {tabs && (
            <div className="page-container-tabs">
              {tabs}
            </div>
          )}
        </div>
      )}

      <div className="page-container-content">{children}</div>

      {footer && <div className="page-container-footer">{footer}</div>}
    </div>
  );
};

export default PageContainer;
